import {
  leadTimeConfigSchema,
  leadTimeConfigUpdateSchema,
} from '@snap-types/models/lead-time-config';
import {
  priceSegmentConfigUpdateSchema,
  priceSegmentConfigWithRelationsSchema,
} from '@snap-types/models/price-segment-config';
import { z } from 'zod/v4';

import {
  capPriceSegmentationColumns,
  columns,
  fixedCustomerColumns,
  fixedDefaultColumns,
  fixedDiscountColumns,
  fixedMarketColumns,
  newPartColumns,
  newPartUserCostColumns,
  newPartUserPriceColumns,
  priceSegmentationColumns,
  rafFixedMarketColumns,
  rafTargetGrossMarginColumns,
  targetContributionMarginColumns,
  targetGrossMarginColumns,
  userImportColumns,
  userPriceColumns,
} from './columns';

export const priceStrategyConfigSchema = z.object(columns);

export type PriceStrategyConfig = z.output<typeof priceStrategyConfigSchema>;

const priceStrategyConfigWithRelationsBaseSchema =
  priceStrategyConfigSchema.extend({
    leadTimeConfig: leadTimeConfigSchema.nullish(),
  });

export const priceStrategyConfigWithRelationsSchema = z.discriminatedUnion(
  'priceStrategyType',
  [
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...fixedCustomerColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...fixedDefaultColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...fixedDiscountColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...fixedMarketColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...targetGrossMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...targetContributionMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...priceSegmentationColumns.shape,
      // @NOTE(shawk): z.unknown() is a workaround for zod type inference issues
      priceSegmentConfigs: z
        .unknown()
        .pipe(z.array(priceSegmentConfigWithRelationsSchema))
        .default([]),
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...capPriceSegmentationColumns.shape,
      // @NOTE(shawk): z.unknown() is a workaround for zod type inference issues
      priceSegmentConfigs: z
        .unknown()
        .pipe(z.array(priceSegmentConfigWithRelationsSchema))
        .default([]),
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...newPartUserPriceColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...newPartUserCostColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...rafFixedMarketColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...rafTargetGrossMarginColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...newPartColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...userImportColumns.shape,
    }),
    z.object({
      ...priceStrategyConfigWithRelationsBaseSchema.shape,
      ...userPriceColumns.shape,
    }),
  ],
);

export type PriceStrategyConfigWithRelations = z.output<
  typeof priceStrategyConfigWithRelationsSchema
>;

const priceStrategyConfigUpdateBaseSchema = priceStrategyConfigSchema
  .pick({
    sortOrder: true,
    enabled: true,
  })
  .extend({
    leadTimeConfig: leadTimeConfigUpdateSchema.nullish(),
  });

export const priceStrategyConfigUpdateSchema = z.discriminatedUnion(
  'priceStrategyType',
  [
    priceStrategyConfigUpdateBaseSchema.extend(fixedCustomerColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(fixedDefaultColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(fixedDiscountColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(fixedMarketColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(targetGrossMarginColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(
      targetContributionMarginColumns.shape,
    ),
    priceStrategyConfigUpdateBaseSchema.extend({
      ...capPriceSegmentationColumns.shape,
      priceSegmentConfigs: z
        .array(priceSegmentConfigUpdateSchema)
        .min(1, { error: 'At least one price segment config is required' }),
    }),
    priceStrategyConfigUpdateBaseSchema.extend({
      ...priceSegmentationColumns.shape,
      priceSegmentConfigs: z
        .array(priceSegmentConfigUpdateSchema)
        .min(1, { error: 'At least one price segment config is required' }),
    }),
    priceStrategyConfigUpdateBaseSchema.extend(newPartColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(newPartUserPriceColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(newPartUserCostColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(rafFixedMarketColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(
      rafTargetGrossMarginColumns.shape,
    ),
    priceStrategyConfigUpdateBaseSchema.extend(userImportColumns.shape),
    priceStrategyConfigUpdateBaseSchema.extend(userPriceColumns.shape),
  ],
);

export type PriceStrategyConfigUpdate = z.output<
  typeof priceStrategyConfigUpdateSchema
>;
